import { Adobe } from './trackerType/Adobe';
import { GoogleAds } from './trackerType/GoogleAds';
import { GoogleAnalytics } from './trackerType/GoogleAnalytics';
import { GoogleTagManager } from './trackerType/GoogleTagManager';
import { Snowplow } from './trackerType/Snowplow';
import { MetaPixel } from './trackerType/MetaPixel';
import { Snapchat } from './trackerType/Snapchat';
import { TikTok } from './trackerType/TikTok';
import { Twitter } from './trackerType/Twitter';
import DateHelper from '~/helpers/DateHelper';
import { TrackerHistoryAttributes } from '~/models/TrackerHistory';
import TemplateApiClient from '~/apiClients/TemplateApiClient';
import GroupTrackerApiClient from '~/apiClients/GroupTrackerApiClient';
import { GroupTracker } from '~/models/GroupTracker';
import { SoftwareTracker } from '~/models/SoftwareTracker';
import { Software } from '~/models/Software';
import { Emarsys } from '~/models/trackerType/Emarsys';
import { ShiftDigital } from '~/models/trackerType/ShiftDigital';
import { TrackerSettingExtraFieldConfig } from '~/models/TrackerSettingExtraFields';

export interface TrackerAttributes {
  id: number;
  type: number;
  name: string;
  version: string;
  isTemplate: boolean;
  originalId?: number;
  softwareId: number;
  snapshot: object;
  trackerHistories?: Array<TrackerHistoryAttributes>,
  groupTrackers: Array<GroupTracker>,
  softwareTrackers: Array<SoftwareTracker>,
  software: Software,
  createdAt: string;
  updatedAt?: string;
  autoSync: number;
  publishedGroupCount?: number;
  groupTrackersCount?: number;
}

export interface TrackerOperationAttributes {
  id?: number,
  softwareId?: number,
  name: string,
  version: string,
  type: number,
}

export interface TrackerType {
  id: number;
  label: string;
  isEnabled: boolean;
  hasClientId: boolean;
  getExtraFields(): TrackerSettingExtraFieldConfig[];
}

export interface TrackerTypeWithClientId extends TrackerType {
  isRequireClientId: boolean;
  getClientIdRegexp(): RegExp;
}

export class Tracker {
  public static TYPE_GOOGLE_TAG_MANAGER = 1;
  public static TYPE_GOOGLE_ANALYTICS = 2;
  public static TYPE_ADOBE = 3;
  public static TYPE_GOOGLE_ADS = 4;
  public static TYPE_SNOWPLOW = 5;
  public static TYPE_EMARSYS = 6;
  public static TYPE_META_PIXEL = 7;
  public static TYPE_SNAPCHAT = 8;
  public static TYPE_TWITTER = 9;
  public static TYPE_TIKTOK = 10;
  public static TYPE_SHIFT_DIGITAL = 11;

  constructor(private readonly attributes: TrackerAttributes) {
  }

  get id(): number {
    return this.attributes.id;
  }

  get lastModified(): string {
    return DateHelper.format(this.attributes?.updatedAt ?? this.attributes.createdAt);
  }

  get name(): string {
    return this.attributes.name;
  }

  get typeLabel(): string {
    return Tracker.getTypeById(this.attributes.type).label;
  }

  get typeId(): number {
    return Tracker.getTypeById(this.attributes.type).id;
  }

  get autoSync(): number {
    return this.attributes.autoSync;
  }

  get fullName(): string {
    return `${this.name} (${this.typeLabel})`;
  }

  get publishedGroupCount(): number {
    return this.attributes.publishedGroupCount;
  }

  get groupTrackersCount(): number {
    return this.attributes.groupTrackersCount;
  }

  get version(): string {
    return this.attributes.version;
  }

  get isTemplate(): boolean {
    return this.attributes.isTemplate;
  }

  get isTemplateChild(): boolean {
    return !this.isTemplate && Boolean(this.originalId);
  }

  get isGroupSpecific(): boolean {
    return !this.isTemplate && !this.isTemplateChild;
  }

  get originalId(): number | undefined {
    return this.attributes.originalId;
  }

  get softwareId(): number {
    return this.attributes.softwareId;
  }

  get snapshot(): object {
    return this.attributes.snapshot;
  }

  get hasHistory(): boolean {
    return Boolean(this.attributes.trackerHistories?.length);
  }

  get relationshipType(): string {
    if (this.isTemplate) {
      return 'Template';
    }

    return this.originalId ? 'Template child' : 'Group/Software specific';
  }

  get groupTrackers(): Array<GroupTracker> {
    return this.attributes.groupTrackers || [];
  }

  get softwareTrackers(): Array<SoftwareTracker> {
    return this.attributes.softwareTrackers || [];
  }

  get software(): Software {
    return new Software(this.attributes.software);
  }

  get createdAt(): string {
    return this.attributes.createdAt;
  }

  get updatedAt(): string | undefined {
    return this.attributes.updatedAt;
  }

  static getTypes(): TrackerType[] {
    return [
      new Adobe(),
      new Emarsys(),
      new GoogleAds(),
      new GoogleAnalytics(),
      new GoogleTagManager(),
      new MetaPixel(),
      new Snapchat(),
      new Twitter(),
      new TikTok(),
      new Snowplow(),
      new ShiftDigital(),
    ];
  }

  public async assignedGroupsNumber(id: number): Promise<any> {
    const groups = await (new GroupTrackerApiClient()).provideAssignedGroups(id);
    return groups.length;
  }

  static getTypeById(id: number): TrackerType {
    return Tracker.getTypes().find((type: TrackerType) => type.id === id) as TrackerType;
  }

  public async cloneTemplate(): Promise<Tracker> {
    return await (new TemplateApiClient()).cloneTemplate(this.id);
  }

  public static async createTemplate(formData: {
    name: string,
    version: string,
    type: number
  }): Promise<any> {
    const {name, type, version} = formData;
    const apiClient = new TemplateApiClient();

    return await apiClient.createTemplate({name, type, version});
  }

  public static async getTemplateById(id: number): Promise<Tracker> {
    return await Tracker.getTemplateApiClient().provideTemplate(id);
  }

  public async updateTemplate(data: TrackerOperationAttributes): Promise<Tracker> {
    return await Tracker.getTemplateApiClient().updateTemplate({id: this.attributes.id, ...data});
  }

  private static getTemplateApiClient(): TemplateApiClient {
    return new TemplateApiClient();
  }
}
